import React, { useEffect, useRef } from 'react';
import { Badge, COLOR, SHAPE } from 'baseui/badge';
import { Skeleton } from 'baseui/skeleton';
import type { Theme } from 'baseui/theme';
import { FRAME_THUMBNAIL } from '../../constants';
type FrameThumbnailPropsT = {
  imageUrl: string;
  frameIndex: number;
  currentFrame: number;
  onFrameClick: (index: number) => void;
  onFrameVisible: (index: number) => void;
  getAnnotationsInFrame: (index: number) => React.ReactNode;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  css: (style: any) => string;
  theme: Theme;
};
export function FrameThumbnail({
  imageUrl,
  frameIndex,
  currentFrame,
  onFrameClick,
  onFrameVisible,
  getAnnotationsInFrame,
  scrollContainerRef,
  css,
  theme
}: FrameThumbnailPropsT) {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousCurrentFrameRef = useRef<number>(-1);
  useEffect(() => {
    const element = containerRef.current;
    const scrollContainer = scrollContainerRef.current;
    if (!element || !scrollContainer) {
      return;
    }
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !imageUrl) {
          onFrameVisible(frameIndex);
        }
      });
    }, {
      root: scrollContainer,
      rootMargin: '200px'
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [frameIndex, imageUrl, onFrameVisible, scrollContainerRef]);
  useEffect(() => {
    const element = containerRef.current;
    // Only scroll if currentFrame actually changed (not just a re-render from image loading)
    const didCurrentFrameChange = previousCurrentFrameRef.current !== currentFrame;
    previousCurrentFrameRef.current = currentFrame;
    if (element && currentFrame === frameIndex && didCurrentFrameChange) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [currentFrame, frameIndex]);
  return <div ref={containerRef} onClick={() => onFrameClick(frameIndex)} onKeyDown={event => {
    if (event.key === 'Enter') {
      onFrameClick(frameIndex);
    }
  }} className={css({
    margin: theme.sizing.scale400,
    position: 'relative',
    border: frameIndex === currentFrame ? `${theme.sizing.scale100} solid ${theme.colors.borderAccent}` : `${theme.sizing.scale100} solid transparent`,
    borderRadius: theme.borders.radius300,
    cursor: 'pointer',
    transition: 'opacity,transform 100ms ease-out',
    ':hover': {
      opacity: 0.8,
      transform: `scale(1.05)`
    }
  })} role="button" tabIndex={frameIndex}>
      <Badge content={frameIndex + 1} shape={SHAPE.rectangle} color={COLOR.primary}>
        {imageUrl ? <img src={imageUrl} className={css({
        width: FRAME_THUMBNAIL.WIDTH,
        height: FRAME_THUMBNAIL.HEIGHT,
        objectFit: 'cover',
        display: 'block'
      })} alt={`frame-${frameIndex}`} /> : <Skeleton width={FRAME_THUMBNAIL.WIDTH} height={FRAME_THUMBNAIL.HEIGHT} animation overrides={{
        Root: {
          style: {
            borderRadius: theme.borders.radius300
          }
        }
      }} />}
      </Badge>
      {getAnnotationsInFrame(frameIndex)}
    </div>;
}